import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Cookie, Mail, MapPin, Building2, Moon, Sun } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

export default function CookiePolicy() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true' || 
                   window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
    
    const savedLang = localStorage.getItem('language');
    if (savedLang && (savedLang === 'en' || savedLang === 'ar')) {
      setLanguage(savedLang);
    }
    
    window.scrollTo(0, 0);
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-background text-foreground ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            className="flex items-center gap-2"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleDarkMode}
              className="rounded-full"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <img src="/logo.png" alt="Mid2Bid" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/10 flex items-center justify-center">
              <Cookie className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">Cookie Policy</h1>
            <p className="text-muted-foreground">Last updated: March 8, 2026</p>
          </div>

          {/* Company Info Card */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Data Controller
            </h2>
            <div className="text-sm text-muted-foreground space-y-1">
              <p><strong>Company:</strong> PASTORIA PROJECT SL</p>
              <p><strong>CIF:</strong> B57664179</p>
              <p className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                Cami Son Rapinya 8 1A, 07013 Palma, Balearic Islands, Spain
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                mid2bid@pastoria.es
              </p>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
            
            <section>
              <h2 className="text-xl font-semibold mb-3">1. What Are Cookies?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files that are placed on your device when you visit a website. They are 
                widely used to make websites work more efficiently and to provide information to the owners of 
                the site. This Cookie Policy explains how PASTORIA PROJECT SL ("we", "us", or "our") uses 
                cookies and similar technologies on the Mid2Bid platform, in compliance with EU Directive 
                2002/58/EC (ePrivacy Directive), GDPR (EU) 2016/679, and Spanish Law 34/2002 (LSSI-CE).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Types of Cookies We Use</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-3">2.1 Strictly Necessary Cookies</h3>
              <p className="text-muted-foreground mb-3">
                These cookies are essential for the website to function properly. They cannot be switched off 
                in our systems. They are usually only set in response to actions made by you, such as setting 
                your privacy preferences, logging in, or filling in forms.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left border-b border-border">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b border-border">Purpose</th>
                      <th className="px-4 py-2 text-left border-b border-border">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="px-4 py-2 border-b border-border">auth_token</td>
                      <td className="px-4 py-2 border-b border-border">User authentication and session management</td>
                      <td className="px-4 py-2 border-b border-border">Session / 7 days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-border">cookie_consent</td>
                      <td className="px-4 py-2 border-b border-border">Stores your cookie preferences</td>
                      <td className="px-4 py-2 border-b border-border">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-border">csrf_token</td>
                      <td className="px-4 py-2 border-b border-border">Security - prevents cross-site request forgery</td>
                      <td className="px-4 py-2 border-b border-border">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-6 mb-3">2.2 Functional Cookies</h3>
              <p className="text-muted-foreground mb-3">
                These cookies enable enhanced functionality and personalization. They may be set by us or by 
                third-party providers whose services we have added to our pages.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left border-b border-border">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b border-border">Purpose</th>
                      <th className="px-4 py-2 text-left border-b border-border">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="px-4 py-2 border-b border-border">language</td>
                      <td className="px-4 py-2 border-b border-border">Remembers your language preference (EN/AR)</td>
                      <td className="px-4 py-2 border-b border-border">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-border">darkMode</td>
                      <td className="px-4 py-2 border-b border-border">Remembers your theme preference (light/dark)</td>
                      <td className="px-4 py-2 border-b border-border">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b border-border">preferred_country</td>
                      <td className="px-4 py-2 border-b border-border">Remembers your default GCC country filter</td>
                      <td className="px-4 py-2 border-b border-border">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-6 mb-3">2.3 Analytics Cookies</h3>
              <p className="text-muted-foreground mb-3">
                These cookies help us understand how visitors interact with our website by collecting and 
                reporting information anonymously. This helps us improve our Service.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left border-b border-border">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b border-border">Purpose</th>
                      <th className="px-4 py-2 text-left border-b border-border">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="px-4 py-2 border-b border-border">_mid2bid_session</td>
                      <td className="px-4 py-2 border-b border-border">Internal analytics - tracks page views and feature usage</td>
                      <td className="px-4 py-2 border-b border-border">30 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Third-Party Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use third-party services that may set cookies on your device:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left border-b border-border">Provider</th>
                      <th className="px-4 py-2 text-left border-b border-border">Purpose</th>
                      <th className="px-4 py-2 text-left border-b border-border">Privacy Policy</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="px-4 py-2 border-b border-border">Stripe</td>
                      <td className="px-4 py-2 border-b border-border">Payment processing and fraud prevention</td>
                      <td className="px-4 py-2 border-b border-border">
                        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">stripe.com/privacy</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Your Cookie Choices</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Under Spanish Law 34/2002 (LSSI-CE) and the EU ePrivacy Directive, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Accept or reject cookies:</strong> When you first visit our site, you will see a cookie banner allowing you to accept all cookies, reject non-essential cookies, or customize your preferences.</li>
                <li><strong>Change your preferences:</strong> You can change your cookie preferences at any time by clicking "Cookie Settings" in the footer of any page.</li>
                <li><strong>Browser settings:</strong> You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies.</li>
                <li><strong>Delete cookies:</strong> You can delete cookies that have already been set at any time through your browser settings.</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                <strong>Note:</strong> If you disable essential cookies, some parts of the Service may become inaccessible or not function properly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. How to Manage Cookies in Your Browser</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Most web browsers allow you to control cookies through their settings. Here are links to 
                instructions for common browsers:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Local Storage and Similar Technologies</h2>
              <p className="text-muted-foreground leading-relaxed">
                In addition to cookies, we may use local storage (localStorage) to store your preferences 
                locally on your device. This includes your language preference, theme settings, and other 
                user interface preferences. Local storage data remains on your device until you clear it 
                through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Updates to This Cookie Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or 
                for legal, operational, or regulatory reasons. We will notify you of any material changes 
                by updating the "Last updated" date at the top of this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-muted/50 rounded-lg p-4 mt-3 text-sm">
                <p><strong>PASTORIA PROJECT SL</strong></p>
                <p>Cami Son Rapinya 8 1A</p>
                <p>07013 Palma, Balearic Islands, Spain</p>
                <p>CIF: B57664179</p>
                <p className="mt-2">Email: <a href="mailto:mid2bid@pastoria.es" className="text-primary hover:underline">mid2bid@pastoria.es</a></p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <LegalFooter language={language} />

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 py-6 px-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="Mid2Bid" className="h-8 w-auto" />
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <a href="/privacy-policy" className="hover:text-primary">Privacy Policy</a>
            <a href="/cookie-policy" className="hover:text-primary">Cookie Policy</a>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 Mid2Bid • All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}
